import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {
  LoadingButton,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  NewNumberInputField,
  NewTextInputField,
} from '@edna/components';
import {useOnChange} from '@edna/hooks';

import {LeftButton} from 'src/components/primitives';
import {
  useGetTenantLeadDetailsQuery,
  useSaveTenantLeadDetailsMutation,
} from 'src/containers/LeadCompanyClients/api';
import {joiResolver} from 'src/utils/validator';

type TProps = {
  closeModal: TEmptyFunction;
};

const resolver = joiResolver((joi, mixed) =>
  joi.object({
    contactName: joi.string().required(),
    email: mixed.email,
    phone: joi.string().max(15).required(),
  }),
);

type TFormData = {
  contactName: string;
  email?: string;
  phone: string;
};

const ContactDetailsModal = React.memo<TProps>(({closeModal}) => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const {data: tenantDetails} = useGetTenantLeadDetailsQuery(supportTenantId);
  const [saveTenantLeadDetails, {isSuccess}] = useSaveTenantLeadDetailsMutation();

  const defaultValues = React.useMemo(
    () => ({
      contactName: tenantDetails?.contactName ?? '',
      email: tenantDetails?.email ?? '',
      phone: tenantDetails?.phone ?? '',
    }),
    [tenantDetails],
  );

  const methods = useForm<TFormData>({
    mode: 'all',
    defaultValues,
    resolver,
  });

  useOnChange(() => methods.reset(defaultValues), [defaultValues]);

  useOnChange(() => {
    if (isSuccess) {
      closeModal();
    }
  }, [isSuccess]);

  const submitContactDetailsForm = React.useCallback(
    async (values: TFormData) => {
      if (tenantDetails && supportTenantId !== undefined) {
        saveTenantLeadDetails({tenantLeadId: tenantDetails.id, supportTenantId, data: values});
      }
    },
    [tenantDetails, saveTenantLeadDetails, supportTenantId],
  );

  return (
    <Modal width="582px">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(submitContactDetailsForm)}>
          <ModalHeader>{t('CompanyDetails:editContactInfo')}</ModalHeader>
          <ModalContent>
            <NewTextInputField name="contactName" label={t('CompanyDetails:editContactName')} />
            <NewTextInputField name="email" label={t('CompanyDetails:email')} />
            <NewNumberInputField name="phone" label={t('CompanyDetails:phone')} />
            <ModalActions>
              <LeftButton onClick={closeModal}>{t('Common:button.cancel')}</LeftButton>
              <LoadingButton appearance="primary" type="submit" data-selector="Common:button.save">
                {t('Common:button.change')}
              </LoadingButton>
            </ModalActions>
          </ModalContent>
        </form>
      </FormProvider>
    </Modal>
  );
});

export default ContactDetailsModal;
